// eslint-disable-next-line eslint-comments/disable-enable-pair
/* eslint-disable formatjs/no-literal-string-in-jsx */
import { PageLayout } from "#/features/global-ui/page-layout"
import { PageNotFound } from "#/components/views/error/PageNotFound"
import { Title } from "#/services/page-title/Title"
import { Paths } from "#/utils/Paths"
import { reportErrorMessage } from "#/utils/ErrorReporting"
import { Alert, Box, Button, Progress } from "@carrotfertility/carotene-core"
import { IntakeForm, PhotoIdUpload, SidebarAppointmentsTile, useTelemedicineAccess } from "@internal/telemedicine"
import { ButtonSkeleton, Skeleton, useNow } from "@internal/ui"
import React from "react"
import { Redirect, useParams } from "react-router-dom"
import type { IntakeSubmittedLocationState } from "#/pages/telemedicine/IntakeSubmittedDialog"
import { usePreVisitIntake } from "./hooks/usePreVisitIntake"
import { useQuery } from "@tanstack/react-query"
import {
  APPOINTMENTS_QUERY_KEY,
  APPOINTMENTS_SHARED_STALE_MS,
  hasEnded
} from "@internal/constants/telemedicine/appointments"
import { trpc } from "#/lib/trpc/client"
import { IntakeTrackingProvider } from "./IntakeTrackingProvider"
import { PreVisitFormTracking } from "./PreVisitFormTracking"
import { PreVisitIntakeTracking } from "./PreVisitIntakeTracking"

const IntakeSkeleton = () => (
  <Box
    role="status"
    aria-label="Loading your intake"
    aria-busy="true"
    sx={(theme) => ({
      marginBlockStart: theme.tokens.spacing.md,
      display: "flex",
      flexDirection: "column",
      gap: theme.spacing(theme.tokens.spacing.lg)
    })}
  >
    <Skeleton width="50%" height={32} />
    {Array.from({ length: 3 }, (_, i) => (
      <Box
        key={i}
        sx={(theme) => ({ display: "flex", flexDirection: "column", gap: theme.spacing(theme.tokens.spacing.xs) })}
      >
        <Skeleton width="40%" height={16} />
        <Skeleton width="100%" height={48} sx={(theme) => ({ borderRadius: theme.tokens.borderRadius.md })} />
      </Box>
    ))}
    <ButtonSkeleton width={144} />
  </Box>
)

function RetryError({ onRetry, isRetrying = false }: { onRetry: () => void; isRetrying?: boolean }): JSX.Element {
  return (
    <Box sx={{ display: "flex", flexDirection: "column", alignItems: "flex-start" }}>
      <Alert severity="error">Something went wrong. Please try again.</Alert>
      <Button
        color="secondary"
        variant="outlined"
        onClick={onRetry}
        disabled={isRetrying}
        aria-busy={isRetrying}
        endIcon={isRetrying ? <Progress size={20} /> : undefined}
      >
        Try again
      </Button>
    </Box>
  )
}

export function TelemedicinePreVisitIntakePage(): JSX.Element {
  const { id: appointmentId } = useParams<{ id: string }>()
  const access = useTelemedicineAccess()
  const allowed = access.data?.eligible === true && access.data.entry.status !== "single_unqualified"
  const appointments = useQuery({
    queryKey: APPOINTMENTS_QUERY_KEY,
    queryFn: () => trpc.telemedicine.getAppointments.query(),
    enabled: allowed,
    staleTime: APPOINTMENTS_SHARED_STALE_MS,
    refetchOnWindowFocus: true
  })
  const appointment = appointments.data?.upcoming.find((visit) => visit.id === appointmentId)
  const now = useNow(appointment ? new Date(appointment.endTime) : null, "at-the-crossing-only")

  if (access.isLoading) return <IntakeSkeleton />
  if (access.isError || !allowed) return <Redirect to={Paths.HOME} />
  if (appointments.isLoading) return <IntakeSkeleton />
  if (appointments.isError && !appointments.data)
    return <RetryError onRetry={() => void appointments.refetch()} isRetrying={appointments.isFetching} />
  if (!appointment || hasEnded(appointment.endTime, now)) return <PageNotFound />
  return (
    <IntakeTrackingProvider key={appointmentId} appointmentId={appointmentId}>
      <PreVisitContent appointmentId={appointmentId} startTime={appointment.startTime} />
    </IntakeTrackingProvider>
  )
}

function PreVisitContent({ appointmentId, startTime }: { appointmentId: string; startTime: string }) {
  const { appointmentHasStarted, questionnaire, photoId } = usePreVisitIntake(appointmentId, startTime)
  const isShowingIntakeStep =
    !photoId.isLoading &&
    !photoId.hasStatusError &&
    (photoId.isRequired || (!appointmentHasStarted && questionnaire.isReady))

  const submittedState: IntakeSubmittedLocationState = { intakeQuestionnaireSubmitted: true }
  const redirectToConfirmationModal = <Redirect to={{ pathname: Paths.HOME, state: submittedState }} />

  const renderQuestionnaire = (): JSX.Element => {
    const { form } = questionnaire
    if (!form) return <Alert severity="error">No intake form is connected to this appointment.</Alert>
    return (
      <Box sx={(theme) => ({ marginBlockStart: theme.tokens.spacing.md })}>
        <IntakeForm
          key={`${appointmentId}:${form.id}`}
          form={form}
          submitError={questionnaire.hasSubmissionError}
          onError={reportErrorMessage}
          warnBeforeExit
          onSubmit={questionnaire.submitAnswers}
        >
          <PreVisitFormTracking />
        </IntakeForm>
      </Box>
    )
  }

  const renderContent = (): JSX.Element => {
    if (photoId.isLoading) return <IntakeSkeleton />
    if (photoId.hasStatusError) return <RetryError onRetry={photoId.retryStatus} isRetrying={photoId.isRetrying} />
    if (photoId.isRequired) {
      return (
        <>
          <PhotoIdUpload
            onImageAdded={photoId.trackImageAdded}
            onSubmit={photoId.upload}
            submitting={photoId.isUploading}
          />
          {photoId.uploadError && (
            <Alert severity="error">
              ID upload failed: {photoId.uploadError instanceof Error ? photoId.uploadError.message : "unknown error"}
            </Alert>
          )}
        </>
      )
    }
    if (appointmentHasStarted) return <Redirect to={Paths.TELEMEDICINE_APPOINTMENTS} />
    if (questionnaire.isLoading) return <IntakeSkeleton />
    if (questionnaire.isNotFound) return <PageNotFound />
    if (questionnaire.error)
      return <Alert severity="error">Something went wrong loading these questions. Please try again.</Alert>
    if (questionnaire.isComplete)
      return questionnaire.hasForms === false ? <PageNotFound /> : redirectToConfirmationModal
    return renderQuestionnaire()
  }

  return (
    <PageLayout
      variant="sidebar"
      sidebar={<SidebarAppointmentsTile appointmentsPath={Paths.TELEMEDICINE_APPOINTMENTS} />}
    >
      <Title title="Prepare for your visit" />
      {isShowingIntakeStep ? <PreVisitIntakeTracking program={questionnaire.program} /> : null}
      {renderContent()}
    </PageLayout>
  )
}
